import NextLink, { LinkProps as NextLinkProps } from "next/link";
import { useRouter, Locale } from "next/router";
import { ComponentPropsWithoutRef, PropsWithChildren, forwardRef } from "react";
import { UrlObject } from "url";
import localizeHref from "../lib/multilingual/localizeHref";
import { toContentLocale } from "@/i18n/config";

type Url = string | UrlObject | Record<Locale, string | UrlObject>;

/** Link renders the anchor itself, so it also accepts every anchor attribute. */
type LinkProps = Omit<NextLinkProps, "href" | "as"> &
  Omit<ComponentPropsWithoutRef<"a">, keyof NextLinkProps | "href"> & {
    href: Url;
    as?: Url;
    locale?: Locale;
    sourceLocale?: Locale;
  };

const getLocalizedHref = (href: Url, source: Locale, destination: Locale) => {
  if (typeof href === "undefined") return href;

  const s = source;
  const d = destination;
  if (typeof href === "string") return localizeHref(href, s, d);

  if ("en" in href) {
    // A per-language href has no `en-us` key: `/en-us` takes the `en` one (WHO-5766).
    const dHref = href[destination] ?? href[toContentLocale(destination)];
    if (typeof dHref === "string") return localizeHref(dHref, s, d);
    if (!dHref) return href as UrlObject;
    const result = { ...dHref, pathname: dHref.pathname };
    return result as UrlObject;
  }
  const result = { ...href, pathname: href.pathname };
  return result as UrlObject;
};

const Link = forwardRef<HTMLAnchorElement, PropsWithChildren<LinkProps>>(
  ({ sourceLocale, locale, href, as, ...props }, ref) => {
    const router = useRouter();
    const source = sourceLocale ?? router.locale;
    const destination = locale ?? router.locale;

    const localizedHref = getLocalizedHref(href, source, destination);
    const localizedAs = as ? getLocalizedHref(as, source, destination) : as;

    return (
      <NextLink
        ref={ref}
        prefetch={false}
        href={localizedHref}
        as={localizedAs}
        locale={destination}
        {...props}
      />
    );
  }
);

export default Link;
